/**
 * useExport Hook
 *
 * Handles batch decryption and export of baby data.
 * Provides progress tracking for large exports.
 */

import { useState, useCallback } from 'react';
import { eventApi } from '../services/api';
import { getBabyKey, decryptEventData, decryptData } from '../crypto';
import { EventType } from '../types';
import { exportToCSV, type DecryptedEvent } from '../utils/exportCSV';
import { exportToPDF } from '../utils/exportPDF';

export interface ExportOptions {
  babyId: string;
  babyName: string;
  dateOfBirth?: Date;
  dateFrom?: Date;
  dateTo?: Date;
  eventTypes?: EventType[];
  format: 'pdf' | 'csv';
  includeNotes?: boolean;
}

export interface ExportProgress {
  stage: 'idle' | 'fetching' | 'decrypting' | 'generating' | 'done' | 'error';
  current: number;
  total: number;
  message: string;
}

export function useExport() {
  const [progress, setProgress] = useState<ExportProgress>({
    stage: 'idle',
    current: 0,
    total: 0,
    message: '',
  });

  const exportData = useCallback(async (options: ExportOptions) => {
    const {
      babyId,
      babyName,
      dateOfBirth,
      dateFrom,
      dateTo,
      eventTypes,
      format,
      includeNotes = true,
    } = options;

    try {
      // Stage 1: Fetching events
      setProgress({
        stage: 'fetching',
        current: 0,
        total: 0,
        message: 'Fetching events...',
      });

      // Build query params
      const params: Record<string, string> = {};
      if (dateFrom) {
        params.startDate = dateFrom.toISOString();
      }
      if (dateTo) {
        params.endDate = dateTo.toISOString();
      }
      if (eventTypes && eventTypes.length > 0) {
        params.types = eventTypes.join(',');
      }
      params.limit = '10000'; // Get all events

      const response = await eventApi.list(babyId, params);
      const events = response.data?.events || [];

      if (events.length === 0) {
        setProgress({
          stage: 'done',
          current: 0,
          total: 0,
          message: 'No events found for the selected criteria',
        });
        return;
      }

      // Stage 2: Get encryption key
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) {
        throw new Error('Could not find encryption key');
      }

      // Stage 3: Decrypt events
      setProgress({
        stage: 'decrypting',
        current: 0,
        total: events.length,
        message: `Decrypting ${events.length} events...`,
      });

      const decryptedEvents: DecryptedEvent[] = [];

      for (let i = 0; i < events.length; i++) {
        const event = events[i];

        try {
          // Decrypt event data
          const data = await decryptEventData(event.encryptedData, aesKey);

          // Decrypt notes if present
          let notes: string | undefined;
          if (event.encryptedNotes && includeNotes) {
            try {
              notes = await decryptData(event.encryptedNotes, aesKey);
            } catch {
              // Notes decryption failed, skip
            }
          }

          decryptedEvents.push({
            id: event.id,
            type: event.type as EventType,
            timestamp: new Date(event.timestamp),
            data: data as Record<string, unknown>,
            notes,
          });
        } catch (err) {
          console.warn(`Failed to decrypt event ${event.id}:`, err);
          // Continue with other events
        }

        // Update progress every 10 events or at the end
        if (i % 10 === 0 || i === events.length - 1) {
          setProgress({
            stage: 'decrypting',
            current: i + 1,
            total: events.length,
            message: `Decrypting events... ${i + 1}/${events.length}`,
          });
        }
      }

      // Filter by event types if specified (server might not support all filters)
      let filteredEvents = decryptedEvents;
      if (eventTypes && eventTypes.length > 0) {
        filteredEvents = decryptedEvents.filter((e) =>
          eventTypes.includes(e.type)
        );
      }

      // Stage 4: Generate export
      setProgress({
        stage: 'generating',
        current: filteredEvents.length,
        total: filteredEvents.length,
        message: `Generating ${format.toUpperCase()}...`,
      });

      if (format === 'csv') {
        exportToCSV({
          babyName,
          events: filteredEvents,
          includeNotes,
        });
      } else {
        await exportToPDF({
          babyName,
          dateOfBirth,
          events: filteredEvents,
          dateFrom,
          dateTo,
        });
      }

      setProgress({
        stage: 'done',
        current: filteredEvents.length,
        total: filteredEvents.length,
        message: `Successfully exported ${filteredEvents.length} events`,
      });
    } catch (error) {
      console.error('Export failed:', error);
      setProgress({
        stage: 'error',
        current: 0,
        total: 0,
        message: error instanceof Error ? error.message : 'Export failed',
      });
    }
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({
      stage: 'idle',
      current: 0,
      total: 0,
      message: '',
    });
  }, []);

  return {
    exportData,
    progress,
    resetProgress,
    isExporting: progress.stage !== 'idle' && progress.stage !== 'done' && progress.stage !== 'error',
  };
}
